export default class I18n {
	/**
	 * Initialize a new translation instance.
	 *
	 * @param  {string}  key
	 * @return {void}
	 */
	constructor(key = 'translations') {
		this.key = key;
		this._setupUpdateListener();
	}

	/**
	 * Setup listener for translation updates (when new translations are loaded async)
	 */
	_setupUpdateListener() {
		if (typeof window !== 'undefined') {
			window.addEventListener('translations-updated', () => {
				// Translations have been updated - any reactive systems listening
				// will be notified via the same event
				console.log('[I18n] Translations updated');
			});
		}
	}

	/**
	 * Check if translations are loaded
	 *
	 * @return {boolean}
	 */
	isReady() {
		return typeof window !== 'undefined' && window[this.key] && Object.keys(window[this.key]).length > 0;
	}

	/**
	 * Get and replace the string of the given key.
	 *
	 * @param  {string}  key
	 * @param  {object}  replace
	 * @return {string}
	 */
	trans(key, replace = {}) {
		// if key contains whitespace, we don't want to translate it
		if (/\s/.test(key)) {
			return key;
		}

		// If translations aren't loaded yet, return the key as placeholder
		if (!this.isReady()) {
			return key;
		}

		return this._replace(this._extract(key), replace);
	}

	/**
	 * Get and pluralize the strings of the given key.
	 *
	 * @param  {string}  key
	 * @param  {number}  count
	 * @param  {object}  replace
	 * @return {string}
	 */
	trans_choice(key, count = 1, replace = {}) {
		// If translations aren't loaded yet, return the key as placeholder
		if (!this.isReady()) {
			return key;
		}

		const translations = this._extract(key, '|').split('|'); let translation;

		translations.some(t => { translation = this._match(t, count); return translation; });

		translation = translation || ((count > 1 || count === 0) ? translations[1] : translations[0]);

		translation = translation.replace(/\[.*?\]|\{.*?\}/, '');

		return this._replace(translation, replace);
	}

	/**
	 * Match the translation limit with the count.
	 *
	 * @param  {string}  translation
	 * @param  {number}  count
	 * @return {string|null}
	 */
	_match(translation, count) {
		const match = translation.match(/^[\{\[]([^\[\]\{\}]*)[\}\]](.*)/); // eslint-disable-line

		if (!match) {
			return;
		}

		if (match[1].includes(',')) {
			const [from, to] = match[1].split(',', 2);

			if (to === '*' && count >= from) {
				return match[2];
			} else if (from === '*' && count <= to) {
				return match[2];
			} else if (count >= from && count <= to) {
				return match[2];
			}
		}

		return match[1] === count ? match[2] : null;
	}

	/**
	 * Replace the placeholders.
	 *
	 * @param  {string}  translation
	 * @param  {object}  replace
	 * @return {string}
	 */
	_replace(translation, replace) {
		if (typeof translation === 'object') {
			return translation;
		}

		for (const placeholder in replace) {
			const regexReplace = new RegExp(`:${placeholder}`, 'gi');
			translation = translation.toString().replace(regexReplace, replace[placeholder]);
		}

		return translation.toString().trim();
	}

	/**
	 * Extract values from objects by dot notation.
	 *
	 * @param  {string}  key
	 * @param  {mixed}  value
	 * @return {mixed}
	 */
	_extract(key, value = null) {
		// Safety check - if translations object doesn't exist, return fallback
		if (!window[this.key]) {
			return value || key;
		}

		const path = key.toString().split('::');
		const keys = path.pop().toString().split('.');

		if (path.length > 0) {
			path[0] += '::';
		}

		return path.concat(keys).reduce((t, i) => {
			if (t && typeof t === 'object' && i in t) {
				return t[i];
			}
			return value || key;
		}, window[this.key]);
	}

	/**
	 * Check if a translation key exists
	 *
	 * @param  {string}  key
	 * @return {boolean}
	 */
	has(key) {
		if (!this.isReady()) {
			return false;
		}

		const path = key.toString().split('::');
		const keys = path.pop().toString().split('.');

		if (path.length > 0) {
			path[0] += '::';
		}

		let transObj = window[this.key];
		let found = true;

		for (const pathPart of path.concat(keys)) {
			if (!transObj || typeof transObj !== 'object') {
				found = false;
				break;
			}
			transObj = transObj[pathPart];
			if (transObj === undefined) {
				found = false;
				break;
			}
		}

		return found;
	}
}

window.$I18n = new I18n();
